/**
 * Runtime configuration, fetched from `config.json` next to index.html.
 *
 * The file is owned by whoever deployed the app, not by the build: the copy in
 * the repo says `"local"` so a dev checkout always uses IndexedDB and can never
 * touch a remote database, while the deployed copy inside PocketBase's
 * pb_public says `"pocketbase"`. It is deliberately left out of the service
 * worker's precache and fetched with `cache: 'no-store'`, so switching the
 * backend never needs a cache bust.
 */
export interface AppConfig {
  /** Where progress lives. 'local' = IndexedDB on this device only. */
  backend: 'local' | 'pocketbase';
  /** PocketBase origin. '' (the default) means same-origin as the app. */
  pocketbaseUrl: string;
  /**
   * Where the media pack is served from. Overrides the region manifest's
   * packBaseUrl; the `syrinx.packBaseUrl` localStorage key still wins over it.
   */
  packBaseUrl: string;
}

const DEFAULTS: AppConfig = {
  backend: 'local',
  pocketbaseUrl: '',
  packBaseUrl: '',
};

let cached: AppConfig | null = null;

/** Load config.json once. A missing or malformed file falls back to local mode. */
export async function loadConfig(): Promise<AppConfig> {
  if (cached) return cached;
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}config.json`, {
      cache: 'no-store',
    });
    cached = res.ok ? normalize(await res.json()) : { ...DEFAULTS };
  } catch {
    cached = { ...DEFAULTS };
  }
  return cached;
}

/** The already-loaded config. Throws if called before loadConfig() resolves. */
export function config(): AppConfig {
  if (!cached) throw new Error('Config not loaded — call loadConfig() first.');
  return cached;
}

function normalize(raw: unknown): AppConfig {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    backend: r['backend'] === 'pocketbase' ? 'pocketbase' : 'local',
    pocketbaseUrl: str(r['pocketbaseUrl']).replace(/\/$/, ''),
    packBaseUrl: str(r['packBaseUrl']).replace(/\/$/, ''),
  };
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
